import { createContext, useState, useEffect, ReactNode } from 'react';

interface OfflineContextType {
  isOnline: boolean;
  syncPending: boolean;
  conflictCount: number;
  triggerSync: () => void;
}

export const OfflineContext = createContext<OfflineContextType>({
  isOnline: true,
  syncPending: false,
  conflictCount: 0,
  triggerSync: () => {}
});

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncPending, setSyncPending] = useState(false);
  const [conflictCount, _setConflictCount] = useState(0);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const triggerSync = () => {
    if (!isOnline) return;
    setSyncPending(true);
    // TODO: implement actual sync with Dexie and Cloud Functions
    setTimeout(() => {
      setSyncPending(false);
    }, 2000);
  };

  useEffect(() => {
    if (isOnline && syncPending) {
      triggerSync();
    }
  }, [isOnline]);

  return (
    <OfflineContext.Provider value={{ isOnline, syncPending, conflictCount, triggerSync }}>
      {children}
    </OfflineContext.Provider>
  );
}
